import React, { useState, useMemo } from "react";
import {
  Box,
  Avatar,
  Typography,
  IconButton,
  Chip,
  TextField,
  useTheme,
  alpha,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import { getBackgroundColor } from "@/pages/Board/utils";
import { VideoComment, formatVideoTimestamp } from "@/utils/videoUtils";
import { useAppSelector } from "@/app/hooks";

interface VideoCommentItemProps {
  comment: VideoComment;
  isActive: boolean;
  onTimestampClick: (timestamp: number) => void;
  onEdit?: (commentId: string, newText: string) => Promise<void>;
  onDelete?: (commentId: string) => void;
  users?: Array<{ _id: string; fullName: string }>;
  isImageMode?: boolean;
}

const VideoCommentItem: React.FC<VideoCommentItemProps> = ({
  comment,
  isActive,
  onTimestampClick,
  onEdit,
  onDelete,
  users = [],
  isImageMode = false,
}) => {
  const theme = useTheme();
  const isPublicBugPage = window?.location?.pathname.includes('public/bug');
  const { currentUser } = useAppSelector((state) => state.appGlobal);

  // Theme colors based on public/private context
  const themeColors = useMemo(() => {
    if (isPublicBugPage) {
      return {
        containerBg: "#ffffff",
        containerBgActive: "rgba(102, 51, 209, 0.08)",
        containerBgHover: "rgba(102, 51, 209, 0.12)",
        containerHoverBg: "#f5f5f5",
        borderActive: "#6633d1",
        primaryMain: "#6633d1",
        primaryDark: "#5228a8",
        textPrimary: "#333333",
        textSecondary: "#666666",
        mentionBg: "rgba(102, 51, 209, 0.1)",
        errorColor: "#d32f2f",
      };
    }
    return {
      containerBg: theme.palette.background.paper,
      containerBgActive: alpha(theme.palette.primary.main, 0.08),
      containerBgHover: alpha(theme.palette.primary.main, 0.12),
      containerHoverBg: theme.palette.action.hover,
      borderActive: theme.palette.primary.main,
      primaryMain: theme.palette.primary.main,
      primaryDark: theme.palette.primary.dark,
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
      mentionBg: alpha(theme.palette.primary.main, 0.1),
      errorColor: theme.palette.error.main,
    };
  }, [isPublicBugPage, theme]);

  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.comment);
  const [isSaving, setIsSaving] = useState(false);

  const handleTimestampClick = () => {
    if (comment.videoTimestamp !== undefined) {
      onTimestampClick(comment.videoTimestamp);
    }
  };

  const handleSaveEdit = async () => {
    if (!editText.trim() || !onEdit) return;
    setIsSaving(true);
    try {
      await onEdit(comment._id, editText);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditText(comment.comment);
    setIsEditing(false);
  };

  // Render comment text with mention highlighting
  const renderCommentText = (text: string) => {
    let parts: (string | JSX.Element)[] = [text];

    users.forEach((user) => {
      const mention = `@${user.fullName}`;
      parts = parts
        .map((part) => {
          if (typeof part !== "string") return part;
          const splitText = part.split(new RegExp(`(${mention})`, "gi"));
          return splitText.map((segment, index) => {
            if (segment.toLowerCase() === mention.toLowerCase()) {
              return (
                <Typography
                  key={`mention-${user._id}-${index}`}
                  component="span"
                  sx={{
                    color: themeColors.primaryMain,
                    fontWeight: 500,
                    backgroundColor: themeColors.mentionBg,
                    borderRadius: "2px",
                    px: 0.5,
                  }}
                >
                  {segment}
                </Typography>
              );
            }
            return segment;
          });
        })
        .flat();
    });

    return parts;
  };

  return (
    <Box
      onClick={!isImageMode ? handleTimestampClick : undefined}
      sx={{
        p: 1.5,
        borderRadius: 1,
        backgroundColor: isActive
          ? themeColors.containerBgActive
          : themeColors.containerBg,
        borderLeft: `3px solid ${
          isActive ? themeColors.borderActive : "transparent"
        }`,
        cursor: isImageMode ? "default" : "pointer",
        transition: "all 0.2s ease",
        "&:hover": {
          backgroundColor: isActive
            ? themeColors.containerBgHover
            : themeColors.containerHoverBg,
          "& .comment-actions": {
            opacity: 1,
          },
        },
        borderBottom: '1px solid #e0e0e0',
      }}
    >
      {/* Header: Timestamp + Author + Time */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={0.5}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {/* Timestamp Badge - only show for video mode */}
          {!isImageMode && comment.videoTimestamp !== undefined && (
            <Chip
              label={formatVideoTimestamp(comment.videoTimestamp || 0)}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleTimestampClick();
              }}
              sx={{
                backgroundColor: themeColors.primaryMain,
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.7rem",
                height: 20,
                "& .MuiChip-label": {
                  px: 1,
                },
                "&:hover": {
                  backgroundColor: themeColors.primaryDark,
                },
              }}
            />
          )}

          {/* Author Avatar */}
          <Avatar
            sx={{
              width: 22,
              height: 22,
              fontSize: "0.7rem",
              color: "#fff",
              bgcolor: getBackgroundColor(comment.createdBy?._id || comment?.userName || ""),
            }}
          >
            {(comment.createdBy?.fullName || comment?.userName)?.charAt(0)?.toUpperCase() || "U"}
          </Avatar>

          {/* Author Name */}
          <Typography variant="body2" fontWeight={500} fontSize="0.9rem" sx={{ color: themeColors.textPrimary }}>
            {comment?.createdBy?.fullName || comment?.userName || "Unknown"}
          </Typography>

          {/* Created Time */}
          <Typography variant="caption" sx={{ color: themeColors.textSecondary, fontSize: "0.78rem" }}>
            {moment(comment.createdAt).format("MMM DD, h:mm A")}
          </Typography>
        </Box>

        {/* Actions */}
        {comment?.createdBy?._id === currentUser?._id && <Box
          className="comment-actions"
          sx={{
            opacity: 0,
            transition: "opacity 0.2s",
            display: "flex",
            gap: 0.5,
          }}
        >
          {onEdit && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
              }}
              sx={{ p: 0.5 }}
            >
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
          )}
          {onDelete && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(comment._id);
              }}
              sx={{
                p: 0.5,
                "&:hover": { color: themeColors.errorColor },
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 14 }} />
            </IconButton>
          )}
        </Box>}
      </Box>

      {/* Comment Text or Edit Field */}
      {isEditing ? (
        <Box mt={1} onClick={(e) => e.stopPropagation()}>
          <TextField
            fullWidth
            multiline
            size="small"
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            autoFocus
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "0.85rem",
              },
            }}
          />
          <Box display="flex" gap={0.5} mt={0.5} justifyContent="flex-end">
            <IconButton
              size="small"
              onClick={handleCancelEdit}
              disabled={isSaving}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleSaveEdit}
              disabled={isSaving || !editText.trim()}
              color="primary"
            >
              <CheckIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <Typography
          variant="body2"
          sx={{
            ml: 0,
            mt: 0.5,
            fontSize: "0.85rem",
            lineHeight: 1.4,
            color: themeColors.textPrimary,
            wordBreak: "break-word",
          }}
        >
          {renderCommentText(comment.comment)}
        </Typography>
      )}
    </Box>
  );
};

export default VideoCommentItem;
